// My buildings (PORTAL-WORKFLOW.md steps 5–9): every building the company has ordered, with
// its name and address (shown from order: they've paid and it's exclusive to them), the
// pack's progress and how long it stays exclusive. Rows open the building's own page.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Crosshair, Search } from "lucide-react";
import { useApi, type OwnedBuilding } from "../api";
import { PackProgress } from "../components/PackProgress";
import "../dashboard.css";
import "../find.css";
import "../my-buildings.css";

/** "9 April 2027" */
export const longDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

/** One line about exclusivity, for the list and the building page. */
export function exclusivityText(b: OwnedBuilding) {
  const until = longDate(b.exclusivity.until);
  if (b.status === "failed") return "Released";
  if (b.exclusivity.state === "running") return `Exclusive until ${until}`;
  if (b.exclusivity.state === "taken") return `Ended ${until} · now with another installer`;
  return `Ended ${until} · you can renew`;
}

export function MyBuildingsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [rows, setRows] = useState<OwnedBuilding[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .listMyBuildings()
      .then(setRows)
      .catch((e: Error) => setError(e.message));
  }, [api]);

  const open = (orderId: string) => `/properties/${encodeURIComponent(orderId)}`;

  return (
    <div className="page">
      <div className="page-head">
        <h1>My properties</h1>
        <p>Properties you've ordered and how each report is coming along. Open one for its details, the report and how long it stays exclusive to you.</p>
      </div>

      {error && <div className="flash flash-danger">{error}</div>}

      {rows && rows.length === 0 && (
        <div className="card find-empty">
          <h2>No properties yet</h2>
          <p className="card-sub">When you order a pack, the property's name and address appear here straight away, and the pack follows.</p>
          <Link to="/find" className="btn btn-primary">
            <Search size={16} aria-hidden="true" /> Search properties
          </Link>
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="card find-table-card">
          <div className="table-scroll">
            <table className="stats-table find-table">
              <thead>
                <tr>
                  <th className="mine-source-col">
                    <span className="sr-only">From</span>
                  </th>
                  <th>Property</th>
                  <th>Where</th>
                  <th>Report</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  // The whole row opens the building; the name is also a link for keyboards
                  <tr key={b.orderId} className="find-row" onClick={() => navigate(open(b.orderId))}>
                    <td className="mine-source-col">
                      <SourceTag source={b.source} />
                    </td>
                    <td>
                      <Link to={open(b.orderId)} className="find-type find-row-link" onClick={(e) => e.stopPropagation()}>
                        {b.property.name}
                      </Link>
                      <div className="muted find-sub">
                        {b.property.buildingType}
                        {b.property.sizeLabel ? ` · ${b.property.sizeLabel}` : ""}
                      </div>
                    </td>
                    <td>
                      <div>{[b.property.town, b.property.postcode].filter(Boolean).join(", ")}</div>
                      <div className="muted find-sub">{b.property.district}</div>
                    </td>
                    <td>
                      <PackProgress status={b.status} compact />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** Where a property came from, with an instant tooltip (CSS, no browser delay). */
function SourceTag({ source }: { source: OwnedBuilding["source"] }) {
  const target = source === "target";
  const tip = target ? "Requested: you asked for this property by its address" : "Search: you ordered this property from your Search properties shortlist";
  return (
    <span className={`mine-source is-${target ? "target" : "find"}`} data-tip={tip} aria-label={tip} tabIndex={0} onClick={(e) => e.stopPropagation()}>
      {target ? <Crosshair size={16} aria-hidden="true" /> : <Search size={16} aria-hidden="true" />}
    </span>
  );
}
